import { useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileCheck2, Trash2, ReceiptText, RefreshCw, Search, Image } from "lucide-react";
import { ConfirmDialog } from "@/components/panel/ui";
import { Field, SelectField, SettingsCard, StatusPill, Toggle, SectionLabel } from "./ui";

const PAY_METHODS = [["dinheiro", "Dinheiro"], ["pix", "PIX"], ["cartao_debito", "Cartão de débito"], ["cartao_credito", "Cartão de crédito"]];
const ALL_METHODS = PAY_METHODS.map(([m]) => m);
const UFS = ["AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"];

export default function FiscalTab({ form, setField, reload }) {
  const [certPass, setCertPass] = useState("");
  const [uploading, setUploading] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const certRef = useRef(null);
  const logoRef = useRef(null);
  const [enviandoLogo, setEnviandoLogo] = useState(false);

  const faltando = form.missing_fiscal_data || [];

  // Certificado A1 vale um ano e, vencido, trava toda a emissão — por isso o
  // aviso muda de cor conforme a data se aproxima, em vez de só informar.
  const certDias = form.cert_expires_at
    ? Math.ceil((new Date(form.cert_expires_at) - new Date()) / 86400000)
    : null;
  const certTom = certDias === null ? "ok" : certDias < 0 ? "vencido" : certDias <= 30 ? "vencendo" : "ok";
  const CERT_CLS = {
    ok: "bg-emerald-50 border-emerald-200 text-emerald-800",
    vencendo: "bg-amber-50 border-amber-200 text-amber-800",
    vencido: "bg-red-50 border-red-200 text-red-800",
  }[certTom];
  const certAviso = certDias === null ? null
    : certDias < 0 ? `vencido há ${Math.abs(certDias)} dia(s)`
    : certDias === 0 ? "vence hoje"
    : `vence em ${certDias} dia(s)`;

  // Mesmo padrão do checkout: preenche pelo ViaCEP e falha em silêncio,
  // porque sempre dá para digitar à mão.
  const lookupCep = async () => {
    const cep = String(form.fiscal_cep || "").replace(/\D/g, "");
    if (cep.length !== 8) { toast.error("Informe um CEP com 8 dígitos"); return; }
    try {
      const d = await (await fetch(`https://viacep.com.br/ws/${cep}/json/`)).json();
      if (d?.erro) { toast.error("CEP não encontrado"); return; }
      setField("fiscal_logradouro", d.logradouro || "");
      setField("fiscal_bairro", d.bairro || "");
      setField("fiscal_municipio", d.localidade || "");
      setField("fiscal_uf", d.uf || "");
      toast.success("Endereço preenchido pelo CEP");
    } catch { toast.error("Não foi possível consultar o CEP agora"); }
  };

  // dry_run pede à FocusNFe que valide sem gravar — assim o usuário descobre o
  // que está errado antes de criar o cadastro de verdade.
  const syncFocus = async (dryRun) => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/settings/focus/sync", { dry_run: dryRun });
      toast.success(dryRun ? "Validação aprovada pela FocusNFe" : "Empresa sincronizada com a FocusNFe");
      if (!dryRun) reload();
      return data;
    } catch (e) { toast.error(apiError(e)); }
    finally { setSyncing(false); }
  };

  // emissão automática de NFC-e no PDV (nfce_auto_methods null = todas as formas)
  const autoPos = form.nfce_auto_pos !== false;
  const autoMethods = Array.isArray(form.nfce_auto_methods) ? form.nfce_auto_methods : ALL_METHODS;
  const toggleMethod = (m) => setField("nfce_auto_methods", autoMethods.includes(m) ? autoMethods.filter((x) => x !== m) : [...autoMethods, m]);

  /**
   * Logomarca da DANFE.
   *
   * Quem desenha a DANFE é a FocusNFe: a logo vive no cadastro da EMPRESA lá, e
   * o upload já dispara a sincronização — sem ela o arquivo ficaria só aqui e a
   * nota continuaria saindo sem logo, que é o que se quis resolver.
   *
   * PNG até 200x200 é exigência deles, não nossa.
   */
  const uploadLogo = async () => {
    const file = logoRef.current?.files?.[0];
    if (!file) { toast.error("Escolha o arquivo PNG da logomarca"); return; }
    setEnviandoLogo(true);
    try {
      const fd = new FormData();
      fd.append("logo", file);
      const { data } = await api.post("/admin/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      data.synced ? toast.success(data.message) : toast.warning(data.message, { duration: 12000 });
      if (logoRef.current) logoRef.current.value = "";
      reload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setEnviandoLogo(false); }
  };

  const removeLogo = async () => {
    try { await api.delete("/admin/settings/logo"); toast.success("Logomarca removida"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const uploadCert = async () => {
    const file = certRef.current?.files?.[0];
    if (!file) { toast.error("Selecione o arquivo .pfx do certificado"); return; }
    if (!certPass) { toast.error("Informe a senha do certificado"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("certificate", file);
      fd.append("password", certPass);
      const { data } = await api.post("/admin/settings/certificate", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.registered_focusnfe ? "Certificado enviado e registrado na FocusNFe" : "Certificado A1 salvo com segurança");
      setCertPass(""); if (certRef.current) certRef.current.value = "";
      reload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setUploading(false); }
  };

  const removeCert = async () => {
    try { await api.delete("/admin/settings/certificate"); toast.success("Certificado removido"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6">
    <SettingsCard
      title="Emissão fiscal (NF-e / NFC-e)"
      right={<StatusPill ok={form.fiscal_ready} okText="Pronta para emitir" pendingText={form.focusnfe_token_set ? "Falta o certificado A1" : "Falta configurar a FocusNFe"} />}
    >
      {/* Identificação e endereço vão no cadastro da empresa na FocusNFe e são
          impressos na DANFE — por isso o endereço aqui é o do CNPJ, que pode
          diferir do endereço de exibição da loja. */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="settings-fiscal-company">
        <div className="md:col-span-2">
          <Field label="Razão social" value={form.razao_social} onChange={(v) => setField("razao_social", v)}
            hint="O nome legal do CNPJ, como está na Receita — não o nome fantasia." />
        </div>
        <Field label="Inscrição municipal" value={form.inscricao_municipal} onChange={(v) => setField("inscricao_municipal", v)} />
      </div>

      <SectionLabel>Endereço fiscal (do CNPJ)</SectionLabel>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="settings-fiscal-address">
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Field label="CEP" value={form.fiscal_cep} onChange={(v) => setField("fiscal_cep", v)} />
          </div>
          <button type="button" onClick={lookupCep} data-testid="settings-cep-lookup"
            className="inline-flex items-center gap-1.5 border border-slate-200 hover:border-brand-500 hover:text-brand-600 text-slate-600 text-sm font-medium px-3 py-2 rounded-lg">
            <Search size={14} /> Buscar
          </button>
        </div>
        <div className="md:col-span-2">
          <Field label="Logradouro" value={form.fiscal_logradouro} onChange={(v) => setField("fiscal_logradouro", v)} />
        </div>
        <Field label="Número" value={form.fiscal_numero} onChange={(v) => setField("fiscal_numero", v)} />
        <Field label="Complemento" value={form.fiscal_complemento} onChange={(v) => setField("fiscal_complemento", v)} />
        <Field label="Bairro" value={form.fiscal_bairro} onChange={(v) => setField("fiscal_bairro", v)} />
        <div className="md:col-span-2">
          <Field label="Município" value={form.fiscal_municipio} onChange={(v) => setField("fiscal_municipio", v)} />
        </div>
        <SelectField label="UF" value={form.fiscal_uf || ""} onChange={(v) => setField("fiscal_uf", v)} testId="settings-fiscal-uf"
          options={[["", "—"], ...UFS.map((u) => [u, u])]} />
      </div>

      <SectionLabel>Emissão</SectionLabel>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SelectField label="Ambiente" value={form.nfe_environment || "homologacao"} onChange={(v) => setField("nfe_environment", v)} testId="settings-nfe-env"
          options={[["homologacao", "Homologação (testes)"], ["producao", "Produção"]]} />
        {/* Antes ficavam fixas no código; variam conforme a operação e a orientação do contador. */}
        <Field label="Natureza da operação (NF-e)" value={form.natureza_operacao_nfe} onChange={(v) => setField("natureza_operacao_nfe", v)} />
        <Field label="Natureza da operação (NFC-e)" value={form.natureza_operacao_nfce} onChange={(v) => setField("natureza_operacao_nfce", v)} />
      </div>

      {/* Numeração: um par (série, próximo número) por modelo e por ambiente.
          Homologação e produção têm sequências fiscais INDEPENDENTES. Antes havia
          uma série só, valendo para os dois — testar em homologação avançava a
          numeração que valeria em produção, e as duas contas divergiam em
          silêncio até a SEFAZ recusar uma nota no balcão. */}
      <div className="border-t border-slate-100 pt-4 mt-5 space-y-4">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          Numeração fiscal — série e próximo número por ambiente
        </p>

        {[["homologacao", "Homologação"], ["producao", "Produção"]].map(([env, label]) => {
          const ativo = (form.nfe_environment || "homologacao") === env;
          return (
            <div key={env} data-testid={`settings-numeracao-${env}`}
              className={`rounded-lg border p-4 ${ativo ? "border-brand-300 bg-brand-50/40" : "border-slate-200"}`}>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-sm font-semibold text-slate-800">{label}</span>
                {ativo && <span className="text-[11px] font-semibold bg-brand-600 text-white rounded-full px-2 py-0.5">em uso</span>}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[["nfe", "NF-e"], ["nfce", "NFC-e"]].flatMap(([modelo, rotulo]) => [
                  <Field key={`${modelo}-s`} label={`Série ${rotulo}`} type="number"
                    value={form[`${modelo}_serie_${env}`] ?? 1}
                    onChange={(v) => setField(`${modelo}_serie_${env}`, v)} />,
                  <Field key={`${modelo}-n`} label={`Próximo nº ${rotulo}`} type="number"
                    value={form[`${modelo}_proximo_numero_${env}`] ?? 1}
                    onChange={(v) => setField(`${modelo}_proximo_numero_${env}`, v)} />,
                ])}
              </div>
              {/* O aviso importa: mexer nisso depois de já ter emitido não
                  renumera nada — só define de onde a PRÓXIMA série começa. */}
              <p className="text-xs text-slate-500 mt-2.5">
                O próximo número só vale para a primeira nota de cada série. Use-o
                para continuar a numeração de um sistema anterior — depois disso,
                quem conta é o sistema.
              </p>
            </div>
          );
        })}
      </div>

      {/* Usados na nota AVULSA (sem produto) e como reserva quando o produto não
          tem os códigos. Peça ao contador — sem NCM válido a SEFAZ rejeita. */}
      <SectionLabel>Padrões fiscais da nota avulsa</SectionLabel>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="settings-fiscal-defaults">
        <Field label="NCM" value={form.default_ncm} onChange={(v) => setField("default_ncm", v)} />
        <Field label="CFOP" value={form.default_cfop} onChange={(v) => setField("default_cfop", v)} />
        <Field label="Unidade" value={form.default_unidade} onChange={(v) => setField("default_unidade", v)} />
        <Field label="CST/CSOSN" value={form.default_cst} onChange={(v) => setField("default_cst", v)} />
        <Field label="Origem" value={form.default_origem} onChange={(v) => setField("default_origem", v)} />
        <Field label="CST PIS" value={form.default_pis_cst} onChange={(v) => setField("default_pis_cst", v)} />
        <Field label="CST COFINS" value={form.default_cofins_cst} onChange={(v) => setField("default_cofins_cst", v)} />
      </div>
      {/* O CFOP daqui é o de operação DENTRO do estado. Quando a nota vai para
          outra UF, o sistema troca o primeiro dígito sozinho (5→6) — mandar
          5102 para outro estado é recusa certa. Os outros três dígitos são os
          desta configuração e não são tocados. */}
      <p className="text-xs text-slate-500 -mt-2">
        <b>PIS e COFINS:</b> a NF-e exige o grupo dos dois em todo item, mesmo sem tributo a declarar.
        <b> 49</b> (&ldquo;outras operações&rdquo;) autoriza sem declarar — confirme com a contabilidade o código do seu regime.
        O <b>CFOP</b> aqui é o de venda dentro do estado; para outra UF o primeiro dígito é ajustado na emissão.
      </p>

      <SectionLabel>Logomarca na DANFE</SectionLabel>
      <div className="rounded-xl border border-slate-200 p-4" data-testid="settings-danfe-logo">
        <p className="text-sm text-slate-600">
          Aparece no DANFE da NF-e, impresso pela FocusNFe. <b>PNG de até 200×200 pixels</b> —
          é a exigência deles, e arquivo fora disso é recusado antes de subir.
        </p>
        {form.danfe_logo_uploaded && (
          <p className="text-sm text-emerald-700 mt-2 flex items-center gap-1.5" data-testid="settings-logo-current">
            <FileCheck2 size={15} /> {form.danfe_logo_filename || "logomarca enviada"}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <input ref={logoRef} type="file" accept="image/png" data-testid="settings-logo-file"
            aria-label="Arquivo PNG da logomarca"
            className="text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:font-medium" />
          <button type="button" onClick={uploadLogo} disabled={enviandoLogo} data-testid="settings-logo-upload"
            className="inline-flex items-center gap-1.5 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white text-sm font-semibold px-3 py-2 rounded-lg">
            <Image size={14} /> {enviandoLogo ? "Enviando..." : "Enviar logomarca"}
          </button>
          {form.danfe_logo_uploaded && (
            <button type="button" onClick={removeLogo} data-testid="settings-logo-remove"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-red-600 hover:text-red-700 px-2 py-2">
              <Trash2 size={14} /> Remover
            </button>
          )}
        </div>
        {/* Dito de propósito: é a primeira pergunta de quem olha a DANFE e vê a
            razão social onde esperava a marca da loja. */}
        <p className="text-xs text-slate-500 mt-3 leading-snug">
          O <b>nome</b> impresso na DANFE é a <b>razão social</b> — o nome legal do CNPJ. A FocusNFe
          não oferece opção de trocá-lo pelo nome fantasia, e escrever o fantasia no campo de razão
          social faria o cadastro divergir da Receita, o que a SEFAZ recusa. O fantasia já vai no
          cadastro e a própria DANFE o mostra entre parênteses no canhoto
          (&ldquo;Recebemos de RAZÃO SOCIAL (Nome Fantasia)&rdquo;).
        </p>
        {/* A dúvida seguinte, sempre: "subi a logo e a nota continua igual". */}
        <p className="text-xs text-amber-700 mt-2 leading-snug">
          A logo e os dados da empresa valem para <b>notas emitidas daqui em diante</b>. O DANFE de
          uma nota já autorizada é gerado no momento da autorização e não muda — reimprimi-lo traz o
          mesmo arquivo.
        </p>
      </div>

      {/* A SEFAZ emite um CSC por ambiente. Os dois ficam guardados e o sistema
          usa o do ambiente selecionado acima — trocar de ambiente não exige
          recadastrar nada. */}
      <div className="border-t border-slate-100 pt-4 mt-5 space-y-4">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          CSC (NFC-e) — um par por ambiente
        </p>

        {[
          ["homologacao", "Homologação", "csc_id_homologacao", "csc_token_homologacao"],
          ["producao", "Produção", "csc_id_producao", "csc_token_producao"],
        ].map(([env, label, idKey, tokenKey]) => {
          const ativo = (form.nfe_environment || "homologacao") === env;
          return (
            <div key={env} data-testid={`settings-csc-${env}`}
              className={`rounded-lg border p-4 ${ativo ? "border-brand-300 bg-brand-50/40" : "border-slate-200"}`}>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-sm font-semibold text-slate-800">{label}</span>
                {ativo && <span className="text-[11px] font-semibold bg-brand-600 text-white rounded-full px-2 py-0.5">em uso</span>}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="CSC ID" value={form[idKey]} onChange={(v) => setField(idKey, v)} />
                <div className="md:col-span-2">
                  <Field label="Código CSC" mono value={form[tokenKey]} onChange={(v) => setField(tokenKey, v)} />
                </div>
              </div>
            </div>
          );
        })}

        {form.fiscal_ready && !form.nfce_ready && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="settings-nfce-csc-missing">
            NF-e pronta, mas falta o CSC de <b>{(form.nfe_environment || "homologacao") === "producao" ? "produção" : "homologação"}</b>:
            sem ele a NFC-e (cupom) é rejeitada, porque é o CSC que assina o QR Code.
          </p>
        )}

        {(form.nfe_environment || "homologacao") === "producao" && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="settings-producao-warning">
            Ambiente de <b>produção</b>: as notas emitidas têm valor fiscal e não podem ser apagadas — só canceladas na SEFAZ, dentro do prazo.
          </p>
        )}
      </div>

      <div className="border-t border-slate-100 pt-4 mt-5">
        <p className="text-xs font-semibold uppercase text-slate-500">Certificado Digital A1</p>
        {form.cert_uploaded ? (
          <>
          <div className={`flex items-center justify-between border rounded-lg px-4 py-3 mt-2 ${CERT_CLS}`} data-testid="settings-cert-status">
            <span className="flex items-center gap-2 text-sm flex-wrap">
              <FileCheck2 size={16} /> {form.cert_filename}
              <span className="text-xs opacity-80">· enviado em {form.cert_uploaded_at ? new Date(form.cert_uploaded_at).toLocaleDateString("pt-BR") : "—"}</span>
              {form.cert_expires_at && (
                <span className="text-xs font-semibold" data-testid="settings-cert-expiry">
                  · válido até {new Date(form.cert_expires_at).toLocaleDateString("pt-BR")} ({certAviso})
                </span>
              )}
            </span>
            <button onClick={() => setConfirmRemove(true)} data-testid="settings-cert-remove" className="text-red-400 hover:text-red-600 shrink-0" aria-label="Remover certificado"><Trash2 size={15} /></button>
          </div>
          {certTom === "vencido" && (
            <p className="text-xs text-red-700 mt-2" data-testid="settings-cert-expired">
              Certificado vencido: nenhuma nota será autorizada até enviar um novo.
            </p>
          )}
          </>
        ) : (
          <div className="flex flex-wrap items-end gap-3 mt-2">
            <label className="inline-flex items-center gap-2 text-sm font-medium text-brand-600 border border-brand-200 hover:bg-brand-50 rounded-lg px-3 py-2 cursor-pointer">
              <Upload size={16} /> Selecionar .pfx
              <input ref={certRef} type="file" accept=".pfx,.p12" aria-label="Escolher arquivo do certificado (.pfx ou .p12)" className="hidden" data-testid="settings-cert-file" />
            </label>
            <input type="password" value={certPass} onChange={(e) => setCertPass(e.target.value)} placeholder="Senha do certificado"
              aria-label="Senha do certificado digital" data-testid="settings-cert-password"
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100" />
            <button onClick={uploadCert} disabled={uploading} data-testid="settings-cert-upload"
              className="bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-lg">{uploading ? "Enviando..." : "Enviar certificado"}</button>
          </div>
        )}
        <p className="text-xs text-slate-400 mt-2">O certificado é guardado de forma cifrada.</p>
      </div>

      {/* Sincronização virou ação própria: antes só acontecia junto com o upload
          do certificado e uma recusa da FocusNFe não aparecia em lugar nenhum. */}
      <div className="border-t border-slate-100 pt-4 mt-5" data-testid="settings-focus-sync">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase text-slate-500">Cadastro na FocusNFe</p>
            <p className="text-xs text-slate-400 mt-1">
              Envia CNPJ, endereço fiscal, séries, CSC e certificado para a FocusNFe.
            </p>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => syncFocus(true)} disabled={syncing || faltando.length > 0}
              data-testid="settings-focus-validate"
              className="border border-slate-200 hover:border-brand-500 hover:text-brand-600 disabled:opacity-40 text-slate-600 text-sm font-semibold px-3.5 py-2 rounded-lg">
              Validar
            </button>
            <button type="button" onClick={() => syncFocus(false)} disabled={syncing || faltando.length > 0}
              data-testid="settings-focus-sync-btn"
              className="inline-flex items-center gap-1.5 bg-brand-600 hover:bg-brand-700 disabled:opacity-40 text-white text-sm font-semibold px-4 py-2 rounded-lg">
              <RefreshCw size={15} className={syncing ? "animate-spin" : ""} /> {syncing ? "Sincronizando..." : "Sincronizar"}
            </button>
          </div>
        </div>

        {faltando.length > 0 && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mt-3" data-testid="settings-focus-missing">
            Preencha antes de sincronizar: <b>{faltando.join(", ")}</b>.
          </p>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3 text-xs">
          <div className="border border-slate-200 rounded-lg px-3 py-2">
            <p className="text-slate-400">Status</p>
            <p className={`font-semibold ${form.focus_status === "ok" ? "text-emerald-700" : form.focus_status ? "text-red-600" : "text-slate-500"}`}
              data-testid="settings-focus-status">{form.focus_status || "nunca sincronizado"}</p>
          </div>
          <div className="border border-slate-200 rounded-lg px-3 py-2">
            <p className="text-slate-400">ID na FocusNFe</p>
            <p className="font-mono text-slate-700">{form.focus_company_id || "—"}</p>
          </div>
          <div className="border border-slate-200 rounded-lg px-3 py-2">
            <p className="text-slate-400">Sincronizado em</p>
            <p className="text-slate-700">{form.focus_synced_at ? new Date(form.focus_synced_at).toLocaleString("pt-BR") : "—"}</p>
          </div>
        </div>

        {form.focus_status === "erro" && form.focus_message && (
          <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2 mt-3" data-testid="settings-focus-error">
            {form.focus_message}
          </p>
        )}
      </div>
    </SettingsCard>

    <SettingsCard title="Emissão automática de NFC-e (balcão/PDV)" icon={ReceiptText}>
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-slate-800">Emitir NFC-e automaticamente ao finalizar a venda</p>
          <p className="text-xs text-slate-400 mt-0.5">Quando desligado, o vendedor decide emitir a nota manualmente em cada venda.</p>
        </div>
        <Toggle checked={autoPos} onChange={(v) => setField("nfce_auto_pos", v)}
          ariaLabel="Emitir NFC-e automaticamente ao finalizar a venda" testId="settings-nfce-auto" />
      </div>

      {autoPos && (
        <div className="border-t border-slate-100 pt-4 mt-4">
          <SectionLabel>Emitir automático por forma de pagamento</SectionLabel>
          <div className="space-y-2">
            {PAY_METHODS.map(([m, label]) => (
              <div key={m} className="flex items-center justify-between bg-slate-50 border border-slate-100 rounded-lg px-3 py-2.5">
                <span className="text-sm text-slate-700">{label}</span>
                <Toggle checked={autoMethods.includes(m)} onChange={() => toggleMethod(m)} testId={`settings-nfce-method-${m}`} />
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-3">As formas desligadas não emitem sozinhas — ao finalizar a venda o sistema pergunta se deseja emitir a nota (com CPF/CNPJ). <b>Vendas online sempre emitem automaticamente.</b></p>
        </div>
      )}
    </SettingsCard>

      <ConfirmDialog
        open={confirmRemove}
        onClose={() => setConfirmRemove(false)}
        onConfirm={() => { setConfirmRemove(false); removeCert(); }}
        title="Remover certificado A1"
        message="Sem o certificado a loja para de emitir NF-e e NFC-e até você enviar um novo. Deseja remover mesmo assim?"
        confirmLabel="Remover certificado"
      />
    </div>
  );
}
